import { useEffect, useState } from 'react';
import {
  Badge,
  Flex,
  Input,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import UserService from 'services/user.api';
import { User } from 'services/@types';
import { buildContactsParams } from './buildContactsParams';

interface Props {
  businessId: string;
}

export default function BusinessContactsTable({ businessId }: Props) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!businessId) return;
    setLoading(true);
    const params = buildContactsParams(businessId);
    UserService.getUsers(params)
      .then((data) => {
        setUsers((data?.results as User[]) || []);
      })
      .catch((err) => {
        console.error('Failed to fetch contacts:', err);
        setUsers([]);
      })
      .finally(() => setLoading(false));
  }, [businessId]);

  const getName = (u: User) => {
    const parts = [u.firstName || u.name, u.lastName || u.familyName].filter(Boolean);
    return parts.join(' ') || u.email || '-';
  };

  const filtered = search
    ? users.filter((u) => {
        const name = getName(u).toLowerCase();
        const email = (u.email || '').toLowerCase();
        return name.includes(search.toLowerCase()) || email.includes(search.toLowerCase());
      })
    : users;

  return (
    <Card p="30px">
      <Flex justify="space-between" align="center" mb="16px">
        <Text color={textColor} fontSize="xl" fontWeight="700">
          Contacts ({users.length})
        </Text>
        <Input
          placeholder="Search contacts..."
          size="sm"
          maxW="250px"
          borderRadius="8px"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Flex>

      {loading ? (
        <Flex justify="center" py="30px">
          <Spinner color="brand.500" />
        </Flex>
      ) : filtered.length === 0 ? (
        <Text color="gray.400" textAlign="center" py="20px">
          No contacts found
        </Text>
      ) : (
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th color="gray.400">NAME</Th>
              <Th color="gray.400">EMAIL</Th>
              <Th color="gray.400">PHONE</Th>
              <Th color="gray.400">ROLE</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filtered.map((u, i) => (
              <Tr key={u.id || i}>
                <Td>
                  <Text color={textColor} fontSize="sm" fontWeight="500">
                    {getName(u)}
                  </Text>
                </Td>
                <Td>
                  <Text color={textColor} fontSize="sm">
                    {u.email || '-'}
                  </Text>
                </Td>
                <Td>
                  <Text color={textColor} fontSize="sm">
                    {u.phone || '-'}
                  </Text>
                </Td>
                <Td>
                  <Badge colorScheme="teal" fontSize="xs">
                    {u.businesses?.[0]?.role || 'member'}
                  </Badge>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </Card>
  );
}
